import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  EFabricationOrderStatus,
  IFabricationOrder
} from '../../../../store/ducks/fabrication-orders/types';
import { Button } from '@gerdau/hefesto';
import FabricationOrderStatusTag from '../../../../components/FabricationOrderStatusTag';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { Container } from './styles';
import Table from '../../../../components/Table';
import { TFabricationView } from '..';
import { TableMenu } from '@gerdau/hefesto-mais-web';
import DownloadExcelButton from './DownloadExcelButton';
import { TableRowSelection } from 'antd/lib/table/interface';
interface FabricationOrderItemTableDataSource extends IFabricationOrder {
  key: number;
  totalWeight: string;
}

interface IFabricationOrdersTableProps {
  fabricationOrders: IFabricationOrder[];
  setActiveView: (activeView: TFabricationView) => void;
}

const FabricationOrdersTable: React.FC<IFabricationOrdersTableProps> = ({
  fabricationOrders,
  setActiveView
}) => {
  const dispatch = useDispatch();

  const [fabricationOrderItemData, setFabricationOrderItemData] = useState<
    FabricationOrderItemTableDataSource[]
  >([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    'orders-fabrication-corte-dobra'
  );

  const seeDetails = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:ver_detalhes',
        itemDescription,
        event: 'click'
      })
    );
  };

  const getColumns = () => {
    return [
      {
        title: 'Código do pedido',
        dataIndex: 'orderNumber',
        width: '15rem',
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          a.orderNumber < b.orderNumber
            ? -1
            : a.orderNumber > b.orderNumber
            ? 1
            : 0,
        render: (text: string) => <span className="order-number">{text}</span>
      },
      {
        title: 'Contrato',
        dataIndex: 'contractNumber',
        width: '15rem',
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          a.contractNumber < b.contractNumber
            ? -1
            : a.contractNumber > b.contractNumber
            ? 1
            : 0,
        render: (text: string) => <span className="order-number">{text}</span>
      },
      {
        title: 'Status do pedido',
        dataIndex: 'orderStatus',
        width: '12rem',
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          a.orderStatus < b.orderStatus
            ? -1
            : a.orderStatus > b.orderStatus
            ? 1
            : 0,
        render: (orderStatus: EFabricationOrderStatus) => (
          <FabricationOrderStatusTag status={orderStatus} />
        )
      },
      {
        title: 'Data de envio',
        dataIndex: 'sendDate',
        sortDirections: ['ascend' as const],
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          moment(a.sendDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.sendDate, 'YYYY-MM-DD').toDate().getTime(),
        render: (sendDate: string) => (
          <>
            {sendDate
              ? moment(sendDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
              : '-'}
          </>
        )
      },
      {
        title: 'Data desejada',
        dataIndex: 'desiredDate',
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          moment(a.desiredDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.desiredDate, 'YYYY-MM-DD').toDate().getTime(),
        render: (desiredDate: string) => (
          <>
            {desiredDate
              ? moment(desiredDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
              : '-'}
          </>
        )
      },
      {
        title: 'Peso total',
        dataIndex: 'totalWeight',
        sorter: (
          a: FabricationOrderItemTableDataSource,
          b: FabricationOrderItemTableDataSource
        ) =>
          a.totalWeight < b.totalWeight
            ? -1
            : a.totalWeight > b.totalWeight
            ? 1
            : 0,
        render: (totalWeight: number) => <>{totalWeight || ' - '}</>
      },
      {
        title: 'Entregas',
        dataIndex: 'item',
        width: '10rem',
        render: (_: string, data: FabricationOrderItemTableDataSource) => (
          <Button
            name="see-deliveries"
            variant="tertiary"
            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
              event.stopPropagation();
              setActiveView({ view: 'deliveries', data });
              dispatch(
                sendTagging({
                  screenName,
                  itemName: 'button:entregas',
                  itemDescription: 'meusPedidos_corteDobra_entregas',
                  event: 'click'
                })
              );
            }}
            disabled={data.orderStatus == '1'}
          >
            Ver entregas
          </Button>
        )
      },
      {
        title: 'Detalhes',
        dataIndex: 'item',
        width: '10rem',
        render: (_: string, data: FabricationOrderItemTableDataSource) => (
          <Button
            name="see-details"
            variant="tertiary"
            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
              event.stopPropagation();
              setActiveView({ view: 'details', data });
              seeDetails();
            }}
          >
            Ver detalhes
          </Button>
        )
      }
    ];
  };

  const rowSelection: TableRowSelection<FabricationOrderItemTableDataSource> = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (
      newSelectedRowsKeys: React.Key[],
      _selectedRows: FabricationOrderItemTableDataSource[],
      info
    ) => {
      if (info.type === 'all') {
        if (selectedRowKeys.length === fabricationOrderItemData.length) {
          setSelectedRowKeys([]);
        } else {
          setSelectedRowKeys(fabricationOrderItemData.map((item) => item.key));
        }
      } else {
        setSelectedRowKeys(newSelectedRowsKeys);
      }
    },
    type: 'checkbox'
  };

  useEffect(() => {
    const data: FabricationOrderItemTableDataSource[] = fabricationOrders.map(
      (order, idx) => {
        const totalWeight = order.deliveries.reduce(
          (acc, curr) => (curr.totalWeight || 0) + acc,
          0
        );

        return {
          ...order,
          key: idx,
          totalWeight:
            totalWeight > 0
              ? totalWeight.toFixed(2).toString().replace('.', ',') + ' T'
              : ''
        };
      }
    );

    setFabricationOrderItemData(data);
  }, [fabricationOrders]);

  return (
    <Container>
      <TableMenu
        leftComponents={
          <>
            {selectedRowKeys.length > 0 && (
              <span className="selected-orders">
                {selectedRowKeys.length}{' '}
                {selectedRowKeys.length > 1
                  ? 'itens selecionados'
                  : 'item selecionado'}
              </span>
            )}
            {selectedRowKeys.length > 0 && (
              <Button
                name="clean-selected-rows"
                variant="secondary"
                onClick={() => setSelectedRowKeys([])}
              >
                Limpar seleção
              </Button>
            )}
          </>
        }
        rightComponents={
          <DownloadExcelButton
            cleanSelectedItens={() => setSelectedRowKeys([])}
            fabricationOrders={fabricationOrderItemData.filter((item) =>
              selectedRowKeys.includes(item.key)
            )}
          />
        }
      />
      <Table
        columns={getColumns()}
        rowSelection={rowSelection}
        data={fabricationOrderItemData}
        hasPagination
      />
    </Container>
  );
};

export default FabricationOrdersTable;
